/**
 * Boot-time environment validation.
 *
 * CRA exposes only `REACT_APP_*` vars on `process.env`. This module asserts
 * the three we need before the app renders, so a missing key fails fast
 * at startup with a clear message instead of in a deep callback later.
 *
 * Read order:
 * 1. `import { env } from 'lib/env'` returns a frozen, typed object.
 * 2. Components / services consume `env.bffBaseUrl`, `env.adyenClientKey`,
 *    `env.adyenEnvironment`. They never touch `process.env` directly.
 *
 * The exported `requireEnv()` factory is used by tests to stub values via
 * `jest.resetModules()` + `process.env` overrides without leaking into the
 * production codepath.
 */

const ADYEN_ENVIRONMENTS = ['test', 'live', 'live-au'] as const;
type AdyenEnvironment = (typeof ADYEN_ENVIRONMENTS)[number];

/** Default local test customer used when `REACT_APP_CUSTOMER_ID` is unset. */
const DEFAULT_CUSTOMER_ID = 'cart-test-user';

export interface AppEnv {
  /** Base URL of the cart-bff. e.g. `http://localhost:9002` locally. NO trailing slash. */
  bffBaseUrl: string;
  /** Adyen Web SDK client key. Format `test_...` or `live_...`. */
  adyenClientKey: string;
  /** Adyen SDK environment selector. */
  adyenEnvironment: AdyenEnvironment;
  /**
   * Whether this build targets a real (deployed) environment. Defaults to
   * `true` (secure default) unless `REACT_APP_IS_PRODUCTION` is explicitly
   * `'false'`. When `false` (local dev), BFF calls authenticate with a fixed
   * `x-customer-id` test user instead of the GEA Brillion `Token-Id`.
   */
  isProduction: boolean;
  /** Fixed customer id sent as `x-customer-id` in non-production (local) mode. */
  customerId: string;
}

function requireString(key: string, value: string | undefined): string {
  if (!value || value.trim().length === 0) {
    throw new Error(
      `[fpa-payment-app] Missing required environment variable ${key}. ` +
        `Copy .env.example to .env and fill in the value before running \`npm start\`.`,
    );
  }
  return value.trim();
}

function requireAdyenEnvironment(value: string | undefined): AdyenEnvironment {
  const raw = requireString('REACT_APP_ADYEN_ENVIRONMENT', value);
  if (!ADYEN_ENVIRONMENTS.includes(raw as AdyenEnvironment)) {
    throw new Error(
      `[fpa-payment-app] REACT_APP_ADYEN_ENVIRONMENT must be one of ` +
        `${ADYEN_ENVIRONMENTS.join(', ')}; got '${raw}'.`,
    );
  }
  return raw as AdyenEnvironment;
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

/**
 * Parse a boolean-ish env string. Only the literal `'false'`
 * (case-insensitive) turns the flag off; anything else — including an unset
 * value — keeps the secure `true` default so a missing var never silently
 * downgrades a production build to the local test-customer auth path.
 */
function isProductionBuild(value: string | undefined): boolean {
  return (value ?? '').trim().toLowerCase() !== 'false';
}

/**
 * Build an `AppEnv` from an arbitrary process.env-like source. Exposed so
 * tests can stub values without monkey-patching `process.env`.
 */
export function requireEnv(source: NodeJS.ProcessEnv = process.env): AppEnv {
  return Object.freeze({
    bffBaseUrl: stripTrailingSlash(requireString('REACT_APP_BFF_BASE_URL', source.REACT_APP_BFF_BASE_URL)),
    adyenClientKey: requireString('REACT_APP_ADYEN_CLIENT_KEY', source.REACT_APP_ADYEN_CLIENT_KEY),
    adyenEnvironment: requireAdyenEnvironment(source.REACT_APP_ADYEN_ENVIRONMENT),
    isProduction: isProductionBuild(source.REACT_APP_IS_PRODUCTION),
    customerId: (source.REACT_APP_CUSTOMER_ID ?? '').trim() || DEFAULT_CUSTOMER_ID,
  });
}

/**
 * Module-level resolved env. Throws at import time when any required var
 * is missing — by design, so dev finds out before the browser opens.
 */
export const env: AppEnv = requireEnv();
